<template>
    <div
        v-if="visible"
        class="fixed bottom-24 left-4 right-4 z-40 mx-auto max-h-[calc(100vh-8rem)] max-w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden rounded border border-brand-primary-200 bg-white shadow-lg dark:border-brand-primary-700 dark:bg-neutral-900 md:bottom-6 md:left-auto md:right-6 md:max-w-lg"
    >
        <div class="p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <div class="text-base font-semibold text-neutral-900 dark:text-white">
                        Report Bug
                    </div>
                    <div class="mt-1 text-sm leading-5 text-neutral-600 dark:text-neutral-300">
                        Tell us what broke or felt wrong. We will attach your account and current page so we can investigate.
                    </div>
                </div>
                <button
                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-xl leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                    type="button"
                    aria-label="Dismiss bug report"
                    @click="handleDismiss"
                >
                    &times;
                </button>
            </div>

            <div v-if="!submitted" class="space-y-3">
                <div>
                    <label class="mb-2 block text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        What happened?
                    </label>
                    <textarea
                        v-model="description"
                        class="min-h-[112px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-primary-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                        maxlength="3000"
                        placeholder="Describe the bug, what you expected, and anything that looked off."
                        @focus="handleStart"
                    ></textarea>
                </div>

                <div>
                    <label class="mb-2 block text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        Steps to reproduce
                    </label>
                    <textarea
                        v-model="stepsToReproduce"
                        class="min-h-[88px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-primary-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                        maxlength="3000"
                        placeholder="Optional, but helpful: what did you tap or do right before it happened?"
                        @focus="handleStart"
                    ></textarea>
                </div>

                <div v-if="error" class="text-sm text-red-600 dark:text-red-400">
                    {{ error }}
                </div>

                <div class="flex flex-wrap items-center justify-end gap-2">
                    <button
                        class="rounded px-3 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                        type="button"
                        :disabled="submitting"
                        @click="handleDismiss"
                    >
                        Cancel
                    </button>
                    <button
                        class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                        type="button"
                        :disabled="submitting || !canSubmit"
                        @click="handleSubmit"
                    >
                        {{ submitting ? 'Sending...' : 'Send Bug Report' }}
                    </button>
                </div>
            </div>

            <div v-else class="space-y-4">
                <div class="rounded bg-neutral-100 p-3 text-sm leading-5 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                    Thanks. The bug report is saved with your account so we can investigate and follow up if needed.
                </div>
                <div class="flex justify-end">
                    <button
                        class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700"
                        type="button"
                        @click="visible = false"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions} from 'vuex';
import {
  capturePostHogEvent,
  getPostHogDistinctId,
} from '@/utils/posthog';

const promptLocation = 'account_menu';

export default {
  name: 'BugReportPrompt',
  data() {
    return {
      description: '',
      error: null,
      started: false,
      stepsToReproduce: '',
      submitted: false,
      submitting: false,
      visible: false,
    };
  },
  computed: {
    canSubmit() {
      return this.description.trim().length >= 5;
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  methods: {
    ...mapActions({
      storeBugReport: 'bugReport/store',
    }),
    eventProperties(extra = {}) {
      return {
        current_url: window.location.href,
        prompt_location: promptLocation,
        user_workout_count: this.user?.workoutCount ?? null,
        ...extra,
      };
    },
    handleDismiss() {
      this.visible = false;
      capturePostHogEvent('bug_report_prompt_dismissed', this.eventProperties());
    },
    handleOpen() {
      this.description = '';
      this.error = null;
      this.started = false;
      this.stepsToReproduce = '';
      this.submitted = false;
      this.submitting = false;
      this.visible = true;

      capturePostHogEvent('bug_report_prompt_viewed', this.eventProperties());
    },
    handleStart() {
      if (this.started) {
        return;
      }

      this.started = true;
      capturePostHogEvent('bug_report_started', this.eventProperties());
    },
    async handleSubmit() {
      if (!this.canSubmit || this.submitting) {
        return;
      }

      this.error = null;
      this.submitting = true;

      try {
        const response = await this.storeBugReport({
          currentUrl: window.location.href,
          description: this.description.trim(),
          metadata: {
            app_version: this.$store.state.system.settings?.version ?? null,
            route: this.$route?.fullPath ?? null,
            viewport: {
              height: window.innerHeight,
              width: window.innerWidth,
            },
          },
          posthogDistinctId: getPostHogDistinctId(),
          promptLocation,
          stepsToReproduce: this.stepsToReproduce.trim() || null,
        });

        const report = response?.data?.data;

        this.submitted = true;
        capturePostHogEvent('bug_report_submitted', this.eventProperties({
          bug_report_id: report?.id ?? null,
          has_steps_to_reproduce: this.stepsToReproduce.trim().length > 0,
        }));
      } catch (error) {
        this.error = 'We could not save that bug report. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    window.addEventListener('v3me:bug-report-prompt', this.handleOpen);
  },
  beforeUnmount() {
    window.removeEventListener('v3me:bug-report-prompt', this.handleOpen);
  },
};
</script>
